<template>
  <div class="landing">
    <NetworkBackground class="landing__background" />
    <div class="landing__content">
      <div class="landing__topbar">
        <button
          type="button"
          class="landing__burger"
          :title="
            showHistory ? 'Speiseplan anzeigen' : 'Bestellhistorie anzeigen'
          "
          :aria-label="
            showHistory ? 'Speiseplan anzeigen' : 'Bestellhistorie anzeigen'
          "
          :aria-pressed="showHistory ? 'true' : 'false'"
          @click="toggleHistory"
        >
          <img src="../assets/menu.png" alt="Bestellhistorie anzeigen" />
        </button>
        <router-link
          class="landing__info-link"
          to="/info"
          aria-label="Informationen anzeigen"
          title="Informationen anzeigen"
        >
          <svg
            class="landing__info-icon"
            viewBox="0 0 24 24"
            role="presentation"
            aria-hidden="true"
          >
            <circle
              cx="12"
              cy="12"
              r="9"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            />
            <path
              d="M12 11v6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
            <circle cx="12" cy="7" r="1.2" fill="currentColor" />
          </svg>
        </router-link>
        <div class="landing__profile">
          <span class="landing__profile-name">
            {{ profile?.given_name || profile?.name || "Mealorder" }}
          </span>
          <img
            v-if="userImage"
            :src="userImage"
            alt="Profilbild"
            class="landing__profile-image"
          />
          <div v-else class="landing__profile-placeholder">
            {{ profileInitials }}
          </div>
        </div>
      </div>
      <header class="landing__hero">
        <div class="landing__brand">
          <img src="../assets/logo.png" alt="ambiFOX" class="landing__logo" />
        </div>
        <ul class="landing__insight-cards">
          <li class="landing__insight-card">
            <span class="landing__insight-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" role="presentation">
                <rect
                  x="3"
                  y="5"
                  width="18"
                  height="16"
                  rx="3"
                  ry="3"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.6"
                />
                <path
                  d="M8 3v4M16 3v4M3 11h18"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                />
              </svg>
            </span>
            <span class="landing__insight-label">Woche</span>
            <strong class="landing__insight-value">
              {{ menuMeta.inferred_week || "Noch nicht bekannt" }}
            </strong>
          </li>
          <li class="landing__insight-card">
            <span class="landing__insight-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" role="presentation">
                <circle
                  cx="12"
                  cy="12"
                  r="8"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.6"
                />
                <path
                  d="M12 8v4l2.5 2.5"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            <span class="landing__insight-label">Bestellfenster</span>
            <strong class="landing__insight-value">
              {{ formatAllowedWeekdayList() || "nach Freigabe" }}
            </strong>
          </li>
        </ul>
      </header>

      <section v-if="loading" class="landing__state">
        Speiseplan wird geladen …
      </section>
      <section v-else-if="error" class="landing__state landing__state--error">
        {{ error }}
      </section>
      <section v-else>
        <template v-if="!showHistory">
          <div class="landing__menu">
            <article
              class="landing-card"
              v-for="entry in dailyMenuItems"
              :key="entryKey(entry)"
            >
              <div class="landing-card__head">
                <small class="landing-card__weekday">
                  <span class="landing-card__weekday-icon" aria-hidden="true">
                    <svg viewBox="0 0 20 20" role="presentation">
                      <circle
                        cx="10"
                        cy="10"
                        r="8"
                        stroke="currentColor"
                        stroke-width="1.5"
                        fill="none"
                      />
                      <circle cx="10" cy="10" r="2" fill="currentColor" />
                    </svg>
                  </span>
                  {{ entry.day }}
                </small>
                <span class="landing-card__date">{{ formatDate(entry) }}</span>
              </div>
              <h2 class="landing-card__title">
                {{ entry.title || "Noch keine Angaben" }}
              </h2>
              <div
                v-if="entry.details && entry.details.length"
                class="landing-card__details"
              >
                <div
                  v-for="(line, detailIndex) in entry.details"
                  :key="detailIndex"
                  class="landing-card__detail"
                >
                  {{ line }}
                </div>
              </div>
              <p v-if="orderCountdown(entry)" class="landing-card__countdown">
                Bestellschluss in {{ orderCountdown(entry) }}
              </p>
              <div class="landing-card__footer">
                <span class="landing-card__price">
                  <span class="landing-card__price-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" role="presentation">
                      <path
                        d="M16 7H9a3 3 0 0 0 0 6h5a3 3 0 0 1 0 6H8"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linecap="round"
                      />
                      <path
                        d="M12 3v19"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linecap="round"
                      />
                    </svg>
                  </span>
                  {{ entry.price || "Preis folgt" }}
                </span>
                <div class="landing-card__actions">
                  <div class="landing-card__choice">
                    <span class="landing-card__choice-label">Gericht</span>
                    <button
                      type="button"
                      class="landing-card__choice-btn"
                      :class="{
                        'landing-card__choice-btn--active':
                          selectedMealType(entry) === 'daily',
                      }"
                      @click="setMealType(entry, 'daily')"
                    >
                      Tagesgericht
                    </button>
                    <button
                      type="button"
                      class="landing-card__choice-btn"
                      :class="{
                        'landing-card__choice-btn--active':
                          selectedMealType(entry) === 'weekly',
                      }"
                      :disabled="!weeklyMenuItem"
                      @click="setMealType(entry, 'weekly')"
                    >
                      Wochengericht
                    </button>
                  </div>
                  <div class="landing-card__quantity">
                    <span class="landing-card__quantity-label">
                      <span
                        class="landing-card__quantity-icon"
                        aria-hidden="true"
                      >
                        <svg viewBox="0 0 20 20" role="presentation">
                          <rect x="3" y="9" width="14" height="2" rx="1" />
                          <rect x="9" y="3" width="2" height="14" rx="1" />
                        </svg>
                      </span>
                      Anzahl
                    </span>
                    <div class="landing-card__quantity-controls">
                      <button
                        type="button"
                        class="landing-card__quantity-btn"
                        :disabled="!canDecreaseOrderAmount(entry)"
                        @click="decreaseOrderAmount(entry)"
                      >
                        -
                      </button>
                      <span class="landing-card__quantity-value">
                        {{ entryOrderAmount(entry) }}
                      </span>
                      <button
                        type="button"
                        class="landing-card__quantity-btn"
                        :disabled="!canIncreaseOrderAmount(entry)"
                        @click="increaseOrderAmount(entry)"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <button
                    class="landing-card__order"
                    :disabled="
                      !canOrder(entry) ||
                      isOrdering(entry) ||
                      isOrderLocked(entry)
                    "
                    @click="orderMeal(entry)"
                  >
                    <span class="landing-card__order-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" role="presentation">
                        <path
                          d="M5 12h14"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                        />
                        <path
                          d="M13 6l6 6-6 6"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>
                    <span v-if="isOrdering(entry)">Wird gespeichert ...</span>
                    <span v-else-if="isOrderLocked(entry)"
                      >Bestätigt (gesperrt)</span
                    >
                    <span v-else-if="isOrdered(entry)"
                      >Bestellung aktualisieren</span
                    >
                    <span v-else>Für diesen Tag bestellen</span>
                  </button>
                  <button
                    v-if="canCancel(entry)"
                    type="button"
                    class="landing-card__cancel"
                    :disabled="isCanceling(entry)"
                    @click="cancelOrder(entry)"
                  >
                    <span class="landing-card__cancel-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" role="presentation">
                        <path
                          d="M6 6l12 12M18 6l-12 12"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                        />
                      </svg>
                    </span>
                    <span v-if="isCanceling(entry)">Wird storniert ...</span>
                    <span v-else>Bestellung stornieren</span>
                  </button>
                </div>
              </div>
              <p v-if="orderStateMessage(entry)" class="landing-card__status">
                {{ orderStateMessage(entry) }}
              </p>
            </article>
          </div>
          <article
            v-if="weeklyMenuItem"
            class="landing-card landing-card--weekly landing__weekly"
          >
            <div class="landing-card__head">
              <small class="landing-card__weekday">
                <span class="landing-card__weekday-icon" aria-hidden="true">
                  <svg viewBox="0 0 20 20" role="presentation">
                    <circle
                      cx="10"
                      cy="10"
                      r="8"
                      stroke="currentColor"
                      stroke-width="1.5"
                      fill="none"
                    />
                    <circle cx="10" cy="10" r="2" fill="currentColor" />
                  </svg>
                </span>
                {{ weeklyMenuItem.day }}
              </small>
              <span class="landing-card__date">{{
                formatDate(weeklyMenuItem)
              }}</span>
            </div>
            <h2 class="landing-card__title">
              {{ weeklyMenuItem.title || "Wochengericht" }}
            </h2>
            <div
              v-if="weeklyMenuItem.details && weeklyMenuItem.details.length"
              class="landing-card__details"
            >
              <div
                v-for="(line, detailIndex) in weeklyMenuItem.details"
                :key="detailIndex"
                class="landing-card__detail"
              >
                {{ line }}
              </div>
            </div>
            <div class="landing-card__footer landing-card__footer--weekly">
              <span class="landing-card__price">
                <span class="landing-card__price-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" role="presentation">
                    <path
                      d="M16 7H9a3 3 0 0 0 0 6h5a3 3 0 0 1 0 6H8"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                    />
                    <path
                      d="M12 3v19"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                    />
                  </svg>
                </span>
                {{ weeklyMenuItem.price || "Preis folgt" }}
              </span>
              <p class="landing-card__weekly-hint">
                Dieses Gericht steht dir über den Schalter neben jedem
                Bestellbutton zur Auswahl.
              </p>
            </div>
          </article>
        </template>
        <template v-else>
          <section class="landing__history">
            <div class="landing__history-head">
              <h2>Deine Bestellhistorie</h2>
            </div>
            <OrderHistory />
          </section>
        </template>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import axios from "axios";
import { logDevError, logDevWarn } from "@/utils/devLogger";
import {
  ExistingOrder,
  ExistingOrdersResponse,
  MealType,
} from "@/types/orders";
import auth from "../auth";
import OrderHistory from "./OrderHistory.vue";
import NetworkBackground from "../components/NetworkBackground.vue";

const WEEKDAY_LABELS = [
  "Sonntag",
  "Montag",
  "Dienstag",
  "Mittwoch",
  "Donnerstag",
  "Freitag",
  "Samstag",
];
const REMOVED_DAYS = new Set(["Montag", "Freitag"]);
const MIN_ORDER_AMOUNT = 1;
const MAX_ORDER_AMOUNT = 9;
// Bestellschluss am Vortag; muss zur Cron-Zeit von send_message.py passen.
const ORDER_DEADLINE_HOUR = 14;
const DEFAULT_PROFILE = {
  name: "Mealorder Nutzer",
  given_name: "Mealorder",
  family_name: "",
};

function readStoredProfile() {
  try {
    if (localStorage.profile) {
      return JSON.parse(localStorage.profile);
    }
  } catch (err) {
    logDevWarn("Profil konnte nicht gelesen werden.", err);
  }
  return { ...DEFAULT_PROFILE };
}

function readStoredImage() {
  try {
    return localStorage.user_image || "";
  } catch (err) {
    logDevWarn("Profilbild konnte nicht gelesen werden.", err);
    return "";
  }
}

type MenuEntry = {
  day: string;
  date?: string;
  date_iso?: string;
  title?: string;
  details?: string[];
  price?: string;
};

type MenuResponse = {
  menu: MenuEntry[];
  notes?: string[];
  pdf_url?: string;
  inferred_week?: string;
  fetched_at?: string;
};

type OrderState = "idle" | "loading" | "success" | "error" | "canceling";

type ProfileInfo = {
  name?: string;
  given_name?: string;
  family_name?: string;
};

export default defineComponent({
  components: {
    OrderHistory,
    NetworkBackground,
  },
  data() {
    return {
      loading: true,
      error: null as string | null,
      menuItems: [] as MenuEntry[],
      menuMeta: {
        pdf_url: "",
        inferred_week: "",
        fetched_at: "",
      },
      orderState: {} as Record<string, { state: OrderState; message?: string }>,
      mealTypeSelection: {} as Record<string, MealType>,
      existingOrders: {} as Record<string, ExistingOrder | null>,
      orderAmountSelection: {} as Record<string, number>,
      profile: readStoredProfile() as ProfileInfo,
      userImage: readStoredImage(),
      showHistory: false,
      now: Date.now(),
      clockTimer: undefined as number | undefined,
    };
  },
  computed: {
    heroSubtitle(): string {
      if (this.menuMeta.inferred_week) {
        return `Gültig für ${this.menuMeta.inferred_week}. Bestelle die Tagesgerichte direkt mit einem Klick.`;
      }
      return "Bestelle die Tagesgerichte dieser Woche direkt hier – das vorhandene Mailverfahren bleibt unverändert.";
    },
    allowedWeekdays(): number[] {
      const envValue = process.env.VUE_APP_POSS_NEXT_ORDER_DATE;
      if (!envValue) {
        return [1, 2, 3, 4, 5];
      }
      return envValue
        .split(",")
        .map((value: string) => parseInt(value.trim(), 10))
        .filter((value: number) => !Number.isNaN(value));
    },
    dailyMenuItems(): MenuEntry[] {
      return this.menuItems.filter((entry) => {
        if (this.resolveMealType(entry) === "weekly") {
          return false;
        }
        if (entry.day && REMOVED_DAYS.has(entry.day)) {
          return false;
        }
        return true;
      });
    },
    weeklyMenuItem(): MenuEntry | null {
      return (
        this.menuItems.find(
          (entry) => this.resolveMealType(entry) === "weekly"
        ) || null
      );
    },
    profileInitials(): string {
      const first = (this.profile?.given_name || "").charAt(0);
      const last = (this.profile?.family_name || "").charAt(0);
      const fallback = (this.profile?.name || "MO").charAt(0);
      return (first + last || fallback || "MO").toUpperCase();
    },
  },
  mounted() {
    this.fetchMenu();
    this.clockTimer = window.setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },
  beforeUnmount() {
    window.clearInterval(this.clockTimer);
  },
  methods: {
    toggleHistory() {
      this.showHistory = !this.showHistory;
      if (this.showHistory) {
        this.$nextTick(() => {
          const history = this.$el.querySelector(".landing__history");
          history?.scrollIntoView({ behavior: "smooth" });
        });
      }
    },
    async fetchMenu() {
      this.loading = true;
      this.error = null;
      const endpoints = this.buildMenuEndpoints();
      let lastError: string | null = null;

      for (const endpoint of endpoints) {
        try {
          const response = await axios.get<MenuResponse>(
            endpoint,
            this.buildRequestConfig(endpoint)
          );
          this.applyMenuResponse(response.data);
          await this.fetchExistingOrders();
          this.loading = false;
          return;
        } catch (err: any) {
          logDevError(`Menu fetch failed for ${endpoint}`, err);
          lastError = err?.message || String(err);
        }
      }

      this.loading = false;
      this.error = lastError
        ? `Speiseplan konnte nicht geladen werden (${lastError}).`
        : "Speiseplan konnte nicht geladen werden.";
    },
    async fetchExistingOrders() {
      if (!process.env.VUE_APP_BACKEND_URL) {
        return;
      }
      try {
        const response = await axios.get<ExistingOrdersResponse>(
          `${process.env.VUE_APP_BACKEND_URL}/api/v1/orders`,
          auth.getHeader()
        );
        const orders = response.data?._embedded?.items || [];
        const isoToKey: Record<string, string> = {};
        this.menuItems.forEach((entry) => {
          const iso = this.resolveDeliveryIso(entry);
          if (iso) {
            isoToKey[iso] = this.entryKey(entry);
          }
        });
        const nextExistingOrders: Record<string, ExistingOrder | null> = {};
        this.menuItems.forEach((entry) => {
          nextExistingOrders[this.entryKey(entry)] = null;
        });
        const selectionUpdates: Record<string, MealType> = {};
        const amountUpdates: Record<string, number> = {};
        orders.forEach((order) => {
          const normalizedIso = order?.delivery_date
            ? this.convertBackendDateToIso(order.delivery_date)
            : null;
          if (!normalizedIso) {
            return;
          }
          const entryKey = isoToKey[normalizedIso];
          if (!entryKey) {
            return;
          }
          const normalized: ExistingOrder = {
            order_id: order.order_id ?? order.id ?? null,
            delivery_date: normalizedIso,
            meal_type: this.normalizeMealType(order.meal_type),
            confirmed: order.confirmed,
            amount: typeof order.amount === "number" ? order.amount : undefined,
          };
          nextExistingOrders[entryKey] = normalized;
          selectionUpdates[entryKey] = normalized.meal_type;
          if (normalized.amount) {
            amountUpdates[entryKey] = normalized.amount;
          }
        });
        this.existingOrders = nextExistingOrders;
        if (Object.keys(selectionUpdates).length) {
          this.mealTypeSelection = {
            ...this.mealTypeSelection,
            ...selectionUpdates,
          };
        }
        if (Object.keys(amountUpdates).length) {
          this.orderAmountSelection = {
            ...this.orderAmountSelection,
            ...amountUpdates,
          };
        }
      } catch (err) {
        logDevError("Bestellungen konnten nicht geladen werden.", err);
      }
    },
    buildMenuEndpoints(): string[] {
      const endpoints: Array<string | undefined> = [
        process.env.VUE_APP_WEEKLY_MENU_URL,
        process.env.VUE_APP_BACKEND_URL
          ? `${process.env.VUE_APP_BACKEND_URL}/api/v1/menu`
          : undefined,
        "/menu.json",
      ];

      return Array.from(
        new Set(endpoints.filter((item): item is string => Boolean(item)))
      );
    },
    buildRequestConfig(endpoint: string) {
      const isAbsolute = /^https?:\/\//i.test(endpoint);
      if (isAbsolute && localStorage.auth) {
        return auth.getHeader();
      }
      return undefined;
    },
    applyMenuResponse(data: MenuResponse) {
      this.menuItems = this.sortMenuEntries(data.menu || []);
      this.mealTypeSelection = {};
      this.existingOrders = {};
      this.orderAmountSelection = {};
      this.menuMeta = {
        pdf_url: data.pdf_url || "",
        inferred_week: data.inferred_week || "",
        fetched_at: data.fetched_at || "",
      };
    },
    sortMenuEntries(items: MenuEntry[]): MenuEntry[] {
      return [...items].sort((a, b) => {
        const aIso = this.resolveDeliveryIso(a);
        const bIso = this.resolveDeliveryIso(b);
        if (!aIso && !bIso) {
          return a.day.localeCompare(b.day);
        }
        if (!aIso) {
          return 1;
        }
        if (!bIso) {
          return -1;
        }
        return aIso.localeCompare(bIso);
      });
    },
    entryKey(entry: MenuEntry): string {
      return `${entry.day}-${
        entry.date_iso || entry.date || entry.title || "na"
      }`;
    },
    entryExistingOrder(entry: MenuEntry): ExistingOrder | null {
      const key = this.entryKey(entry);
      return this.existingOrders[key] || null;
    },
    entryOrderAmount(entry: MenuEntry): number {
      const key = this.entryKey(entry);
      if (typeof this.orderAmountSelection[key] === "number") {
        return this.orderAmountSelection[key];
      }
      const existingOrder = this.entryExistingOrder(entry);
      if (existingOrder?.amount) {
        return existingOrder.amount;
      }
      return MIN_ORDER_AMOUNT;
    },
    selectedMealType(entry: MenuEntry): MealType {
      const key = this.entryKey(entry);
      if (this.mealTypeSelection[key]) {
        return this.normalizeMealType(this.mealTypeSelection[key]);
      }
      const existingOrder = this.entryExistingOrder(entry);
      if (existingOrder) {
        return this.normalizeMealType(existingOrder.meal_type);
      }
      return "daily";
    },
    setMealType(entry: MenuEntry, mealType: MealType) {
      const key = this.entryKey(entry);
      this.mealTypeSelection = {
        ...this.mealTypeSelection,
        [key]: mealType,
      };
    },
    setOrderAmount(entry: MenuEntry, amount: number) {
      const normalized = Math.min(
        MAX_ORDER_AMOUNT,
        Math.max(MIN_ORDER_AMOUNT, Math.round(amount))
      );
      const key = this.entryKey(entry);
      this.orderAmountSelection = {
        ...this.orderAmountSelection,
        [key]: normalized,
      };
    },
    increaseOrderAmount(entry: MenuEntry) {
      this.setOrderAmount(entry, this.entryOrderAmount(entry) + 1);
    },
    decreaseOrderAmount(entry: MenuEntry) {
      this.setOrderAmount(entry, this.entryOrderAmount(entry) - 1);
    },
    canIncreaseOrderAmount(entry: MenuEntry): boolean {
      return this.entryOrderAmount(entry) < MAX_ORDER_AMOUNT;
    },
    canDecreaseOrderAmount(entry: MenuEntry): boolean {
      return this.entryOrderAmount(entry) > MIN_ORDER_AMOUNT;
    },
    formatDate(entry: MenuEntry): string {
      if (entry.date_iso) {
        return this.formatIsoDisplay(entry.date_iso);
      }

      if (entry.date) {
        return entry.date;
      }

      return "";
    },
    formatTimestamp(value: string): string {
      const date = new Date(value);
      return date.toLocaleString("de-DE", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    canOrder(entry: MenuEntry): boolean {
      return !this.orderConstraintMessage(entry);
    },
    orderConstraintMessage(entry: MenuEntry): string | undefined {
      const isoDate = this.resolveDeliveryIso(entry);
      if (!isoDate) {
        return "Dieses Gericht enthält kein Lieferdatum.";
      }

      const entryDate = this.parseIsoToLocalDate(isoDate);
      if (!entryDate) {
        return "Das Lieferdatum konnte nicht interpretiert werden.";
      }

      if (
        this.allowedWeekdays.length > 0 &&
        !this.allowedWeekdays.includes(entryDate.getDay())
      ) {
        return `Das Backend akzeptiert Bestellungen nur für ${this.formatAllowedWeekdayList()}.`;
      }

      const earliestIso = this.computeEarliestDeliveryIso();
      if (earliestIso && isoDate < earliestIso) {
        return `Bestellungen sind erst ab ${this.formatIsoDisplay(
          earliestIso
        )} möglich (Deadline: Vortag ${ORDER_DEADLINE_HOUR}:00 Uhr).`;
      }

      return undefined;
    },
    orderCountdown(entry: MenuEntry): string | null {
      const isoDate = this.resolveDeliveryIso(entry);
      const entryDate = isoDate ? this.parseIsoToLocalDate(isoDate) : null;
      if (!entryDate || !this.canOrder(entry) || this.isOrderLocked(entry)) {
        return null;
      }
      const deadline = new Date(entryDate);
      deadline.setDate(deadline.getDate() - 1);
      deadline.setHours(ORDER_DEADLINE_HOUR, 0, 0, 0);
      const totalSeconds = Math.floor((deadline.getTime() - this.now) / 1000);
      if (totalSeconds <= 0) {
        return null;
      }
      const days = Math.floor(totalSeconds / 86400);
      const pad = (value: number) => String(value).padStart(2, "0");
      const clock = `${pad(Math.floor((totalSeconds % 86400) / 3600))}:${pad(
        Math.floor((totalSeconds % 3600) / 60)
      )}:${pad(totalSeconds % 60)}`;
      return days > 0 ? `${days} ${days === 1 ? "Tag" : "Tagen"} ${clock}` : clock;
    },
    computeEarliestDeliveryIso(): string | null {
      if (!this.allowedWeekdays.length) {
        return null;
      }

      const now = new Date(this.now);
      const candidate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        12,
        0,
        0,
        0
      );
      candidate.setDate(candidate.getDate() + 1);

      if (now.getHours() >= ORDER_DEADLINE_HOUR) {
        candidate.setDate(candidate.getDate() + 1);
      }

      while (!this.allowedWeekdays.includes(candidate.getDay())) {
        candidate.setDate(candidate.getDate() + 1);
      }

      return candidate.toISOString().split("T")[0];
    },
    parseIsoToLocalDate(iso: string): Date | null {
      const [year, month, day] = iso
        .split("-")
        .map((value) => parseInt(value, 10));
      if (
        [year, month, day].some((value) => Number.isNaN(value)) ||
        !year ||
        !month ||
        !day
      ) {
        return null;
      }

      return new Date(year, month - 1, day, 12, 0, 0, 0);
    },
    formatIsoDisplay(iso: string): string {
      const date = this.parseIsoToLocalDate(iso);
      if (!date) {
        return iso;
      }
      return date.toLocaleDateString("de-DE", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
      });
    },
    formatAllowedWeekdayList(): string {
      if (!this.allowedWeekdays.length) {
        return "";
      }

      const names = this.allowedWeekdays.map(
        (weekday) => WEEKDAY_LABELS[weekday] || `${weekday}`
      );

      if (names.length === 1) {
        return names[0];
      }

      const deduped = [...names];
      const last = deduped.pop();
      if (!last) {
        return "";
      }

      if (!deduped.length) {
        return last;
      }

      return `${deduped.join(", ")} und ${last}`;
    },
    resolveDeliveryIso(entry: MenuEntry): string | null {
      if (entry.date_iso) {
        return entry.date_iso;
      }

      if (entry.date) {
        const cleaned = entry.date.replace(/\s/g, "");
        const [day, month, year] = cleaned.split(".");
        if (day && month && year) {
          return `${year.length === 2 ? `20${year}` : year}-${month.padStart(
            2,
            "0"
          )}-${day.padStart(2, "0")}`;
        }
      }

      return null;
    },
    transformIsoForBackend(isoDate: string): string {
      const [year, month, day] = isoDate.split("-");
      return `${day}-${month}-${year}`;
    },
    convertBackendDateToIso(value: string): string | null {
      if (!value) {
        return null;
      }
      const trimmed = value.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
      }
      const cleaned = trimmed.replace(/\s/g, "");
      const parts = cleaned.split(/[-.]/);
      if (parts.length !== 3) {
        return null;
      }
      const [dayRaw, monthRaw, yearRaw] = parts;
      const day = parseInt(dayRaw, 10);
      const month = parseInt(monthRaw, 10);
      const year = parseInt(
        yearRaw.length === 2 ? `20${yearRaw}` : yearRaw,
        10
      );
      if (
        Number.isNaN(day) ||
        Number.isNaN(month) ||
        Number.isNaN(year) ||
        day <= 0 ||
        day > 31 ||
        month <= 0 ||
        month > 12
      ) {
        return null;
      }
      return `${year.toString().padStart(4, "0")}-${month
        .toString()
        .padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
    },
    resolveMealType(entry: MenuEntry): MealType {
      return entry.day?.toLowerCase().includes("wochengericht")
        ? "weekly"
        : "daily";
    },
    async orderMeal(entry: MenuEntry) {
      const key = this.entryKey(entry);
      const constraint = this.orderConstraintMessage(entry);
      const isoDate = this.resolveDeliveryIso(entry);
      const mealType = this.normalizeMealType(this.selectedMealType(entry));
      const amount = this.entryOrderAmount(entry);
      const existingOrder = this.entryExistingOrder(entry);
      const existingAmount = existingOrder?.amount ?? MIN_ORDER_AMOUNT;
      const existingMealType = existingOrder
        ? this.normalizeMealType(existingOrder.meal_type)
        : null;
      const isEditableExistingOrder = Boolean(
        existingOrder && existingOrder.order_id && !existingOrder.confirmed
      );
      if (!isoDate || constraint) {
        this.setOrderState(
          key,
          "error",
          constraint || "Dieses Gericht enthält kein Lieferdatum."
        );
        return;
      }

      if (
        isEditableExistingOrder &&
        existingMealType === mealType &&
        existingAmount === amount
      ) {
        this.setOrderState(
          key,
          "success",
          "Bestellung ist bereits auf diesem Stand."
        );
        return;
      }

      const loadingMessage = isEditableExistingOrder
        ? "Bestellung wird aktualisiert ..."
        : "Bestellung wird übermittelt ...";
      this.setOrderState(key, "loading", loadingMessage);

      if (isEditableExistingOrder && existingOrder?.order_id) {
        try {
          await axios.delete(
            `${process.env.VUE_APP_BACKEND_URL}/api/v1/orders/${existingOrder.order_id}`,
            auth.getHeader()
          );
          this.existingOrders = {
            ...this.existingOrders,
            [key]: null,
          };
        } catch (err) {
          logDevError(err);
          this.setOrderState(
            key,
            "error",
            "Bestehende Bestellung konnte nicht aktualisiert werden."
          );
          return;
        }
      }

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BACKEND_URL}/api/v1/orders`,
          {
            amount,
            delivery_date: this.transformIsoForBackend(isoDate),
            meal_type: mealType,
          },
          auth.getHeader()
        );
        const createdOrder = this.normalizeOrderPayload(
          response.data,
          isoDate,
          mealType,
          amount
        );
        this.assignExistingOrder(entry, createdOrder);
        const successMessage = isEditableExistingOrder
          ? "Bestellung aktualisiert."
          : "Bestellung erfolgreich verschickt.";
        this.setOrderState(key, "success", successMessage);
      } catch (err) {
        logDevError(err);
        this.setOrderState(
          key,
          "error",
          "Bestellung konnte nicht gesendet werden."
        );
      }
    },
    normalizeOrderPayload(
      payload: any,
      isoDate: string,
      mealType: MealType,
      amount: number
    ): ExistingOrder {
      const normalizedIso = payload?.delivery_date
        ? this.convertBackendDateToIso(payload.delivery_date)
        : null;
      return {
        order_id: payload?.order_id ?? payload?.id ?? null,
        delivery_date: normalizedIso || isoDate,
        meal_type: this.normalizeMealType(payload?.meal_type || mealType),
        confirmed: payload?.confirmed ?? false,
        amount: typeof payload?.amount === "number" ? payload.amount : amount,
      };
    },
    async cancelOrder(entry: MenuEntry) {
      const existingOrder = this.entryExistingOrder(entry);
      const key = this.entryKey(entry);
      if (!existingOrder || !existingOrder.order_id) {
        this.setOrderState(
          key,
          "error",
          "Keine Bestellung zum Stornieren gefunden."
        );
        return;
      }
      this.setOrderState(key, "canceling", "Bestellung wird storniert ...");
      try {
        await axios.delete(
          `${process.env.VUE_APP_BACKEND_URL}/api/v1/orders/${existingOrder.order_id}`,
          auth.getHeader()
        );
        this.clearExistingOrder(entry);
        this.setOrderState(key, "success", "Bestellung storniert.");
      } catch (err) {
        logDevError(err);
        this.setOrderState(key, "error", "Stornierung fehlgeschlagen.");
      }
    },
    setOrderState(key: string, state: OrderState, message?: string) {
      const previous = this.orderState[key] || { state: "idle" as OrderState };
      this.orderState = {
        ...this.orderState,
        [key]: {
          ...previous,
          state,
          message,
        },
      };
    },
    assignExistingOrder(entry: MenuEntry, order: ExistingOrder) {
      const key = this.entryKey(entry);
      const normalizedOrder: ExistingOrder = {
        ...order,
        meal_type: this.normalizeMealType(order.meal_type),
      };
      this.existingOrders = {
        ...this.existingOrders,
        [key]: normalizedOrder,
      };
      this.mealTypeSelection = {
        ...this.mealTypeSelection,
        [key]: normalizedOrder.meal_type,
      };
      if (normalizedOrder.amount) {
        this.orderAmountSelection = {
          ...this.orderAmountSelection,
          [key]: normalizedOrder.amount,
        };
      }
    },
    clearExistingOrder(entry: MenuEntry) {
      const key = this.entryKey(entry);
      this.existingOrders = {
        ...this.existingOrders,
        [key]: null,
      };
      this.orderAmountSelection = {
        ...this.orderAmountSelection,
        [key]: MIN_ORDER_AMOUNT,
      };
    },
    isOrdering(entry: MenuEntry): boolean {
      const key = this.entryKey(entry);
      return this.orderState[key]?.state === "loading";
    },
    isCanceling(entry: MenuEntry): boolean {
      const key = this.entryKey(entry);
      return this.orderState[key]?.state === "canceling";
    },
    isOrdered(entry: MenuEntry): boolean {
      return Boolean(this.entryExistingOrder(entry));
    },
    isOrderLocked(entry: MenuEntry): boolean {
      const existing = this.entryExistingOrder(entry);
      return Boolean(existing && existing.confirmed);
    },
    canCancel(entry: MenuEntry): boolean {
      const existing = this.entryExistingOrder(entry);
      return Boolean(existing && existing.order_id && !existing.confirmed);
    },
    orderStateMessage(entry: MenuEntry): string | undefined {
      const key = this.entryKey(entry);
      if (this.orderState[key]?.message) {
        return this.orderState[key]?.message;
      }
      const existing = this.entryExistingOrder(entry);
      if (existing) {
        const normalizedType = this.normalizeMealType(existing.meal_type);
        const label =
          normalizedType === "weekly" ? "Wochengericht" : "Tagesgericht";
        const amountLabel = `${existing.amount || 1}× ${label}`;
        if (existing.confirmed) {
          return `Bestätigt: ${amountLabel}.`;
        }
        return `Bestellt: ${amountLabel}.`;
      }
      return undefined;
    },
    normalizeMealType(value?: string | null): MealType {
      if (!value) {
        return "daily";
      }
      const normalized = String(value).trim().toLowerCase();
      return normalized === "weekly" ? "weekly" : "daily";
    },
  },
});
</script>

<style scoped>
.landing {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  min-height: 100vh;
  padding: 3rem clamp(1rem, 3vw, 2.75rem) 5rem;
  background: #0c0f1c;
  color: #f7f8fb;
}

.landing__background {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  opacity: 0.9;
}

.landing__content {
  position: relative;
  z-index: 1;
}

.landing__topbar {
  max-width: 1160px;
  margin: 0 auto 2rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  padding: 0.55rem 0.85rem;
  border-radius: 999px;
  background: rgba(8, 12, 26, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 24px 48px rgba(2, 9, 22, 0.65);
  backdrop-filter: blur(18px);
}

.landing__info-link,
.landing__burger {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: linear-gradient(
    150deg,
    rgba(255, 255, 255, 0.08),
    rgba(0, 0, 0, 0.35)
  );
  box-shadow: 0 14px 24px rgba(1, 5, 14, 0.6);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.landing__info-link {
  text-decoration: none;
  color: #fff;
}

.landing__info-link:hover,
.landing__burger:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 32px rgba(0, 0, 0, 0.55);
  border-color: rgba(255, 255, 255, 0.22);
}

.landing__info-icon {
  width: 26px;
  height: 26px;
  color: inherit;
}

.landing__burger {
  border: none;
  background: linear-gradient(
    160deg,
    rgba(255, 255, 255, 0.08),
    rgba(255, 255, 255, 0.02)
  );
}

.landing__burger img {
  width: 26px;
  height: 26px;
}

.landing__profile {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 999px;
  padding: 0.4rem 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.landing__profile-name {
  font-weight: 600;
  letter-spacing: 0.01em;
}

.landing__profile-image,
.landing__profile-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  object-fit: cover;
}

.landing__profile-placeholder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(120deg, var(--ambifox-color), #ff944d);
  color: #060b18;
  font-weight: 700;
}

.landing__hero {
  max-width: 1160px;
  margin: 0 auto 2.5rem;
  text-align: left;
}

.landing__brand {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.landing__logo {
  width: clamp(140px, 18vw, 220px);
  height: auto;
  display: block;
  margin: 0 auto;
}

.landing__insight-cards {
  margin: 2rem 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
}

.landing__insight-card {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 1rem 1.2rem;
  border-radius: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: linear-gradient(
    140deg,
    rgba(13, 22, 38, 0.9),
    rgba(10, 12, 30, 0.85)
  );
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.35);
}

.landing__insight-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.landing__insight-icon svg {
  width: 22px;
  height: 22px;
  color: var(--ambifox-color);
}

.landing__insight-label {
  display: block;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
}

.landing__insight-value {
  display: block;
  margin-top: 0.2rem;
  font-size: 1.25rem;
  color: #fff;
  font-weight: 600;
}

.landing__state {
  text-align: center;
  margin-top: 3rem;
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.75);
}

.landing__state--error {
  color: #ffb0b0;
}

.landing__menu {
  max-width: 1160px;
  margin: 0 auto;
  display: flex;
  flex-wrap: nowrap;
  gap: 1.75rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  scroll-snap-type: x mandatory;
}

.landing-card {
  background: linear-gradient(
    150deg,
    rgba(10, 14, 28, 0.95),
    rgba(8, 10, 24, 0.9)
  );
  border-radius: 1.6rem;
  padding: 1.9rem;
  box-shadow: 0 32px 60px rgba(2, 8, 18, 0.75);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;
  overflow: hidden;
  transition: transform 0.35s ease, box-shadow 0.35s ease,
    border-color 0.35s ease;
  flex: 0 0 340px;
  max-width: 420px;
  scroll-snap-align: start;
}

.landing-card::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(
    130deg,
    rgba(255, 255, 255, 0.06),
    transparent 60%
  );
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.35s ease;
}

.landing-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 40px 80px rgba(1, 6, 18, 0.9);
  border-color: rgba(255, 255, 255, 0.12);
}

.landing-card:hover::after {
  opacity: 1;
}

.landing-card__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.6);
  gap: 0.75rem;
}

.landing-card__weekday {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.landing-card__weekday-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.landing-card__weekday-icon svg {
  width: 15px;
  height: 15px;
  color: rgba(255, 255, 255, 0.65);
}

.landing-card__date {
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.landing-card__title {
  margin: 1.2rem 0 0.6rem;
  font-size: 1.45rem;
  line-height: 1.35;
}

.landing-card__details {
  margin: 0 0 1.4rem 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.landing-card__detail {
  padding: 0.1rem 0;
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.4;
}

.landing-card__footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 1.25rem;
}

.landing-card__price {
  font-weight: 600;
  color: var(--ambifox-color);
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 1.05rem;
}

.landing-card__price-icon {
  width: 32px;
  height: 32px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.landing-card__price-icon svg {
  width: 20px;
  height: 20px;
  color: inherit;
}

.landing-card__actions {
  display: flex;
  align-items: stretch;
  gap: 0.85rem;
  flex-wrap: wrap;
}

.landing-card__choice {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 1rem;
  padding: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.landing-card__choice-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.7);
}

.landing-card__choice-btn {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  padding: 0.3rem 1rem;
  background: rgba(255, 255, 255, 0.02);
  color: inherit;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.85rem;
  transition: all 0.2s ease;
}

.landing-card__choice-btn--active {
  background: linear-gradient(
    120deg,
    rgba(255, 255, 255, 0.12),
    rgba(255, 255, 255, 0.03)
  );
  border-color: var(--ambifox-color);
  color: var(--ambifox-color);
  box-shadow: 0 12px 24px rgba(255, 102, 0, 0.28);
}

.landing-card__choice-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.landing-card__quantity {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 1rem;
  padding: 0.5rem 0.9rem;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.landing-card__quantity-label {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
}

.landing-card__quantity-icon {
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.landing-card__quantity-icon svg {
  width: 18px;
  height: 18px;
  fill: rgba(255, 255, 255, 0.7);
}

.landing-card__quantity-controls {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.landing-card__quantity-btn {
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  border-radius: 12px;
  width: 36px;
  height: 36px;
  font-size: 1.1rem;
  cursor: pointer;
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.06),
    rgba(255, 255, 255, 0.01)
  );
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2);
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    border-color 0.15s ease;
}

.landing-card__quantity-btn:not(:disabled):hover {
  transform: translateY(-1px);
  border-color: rgba(255, 255, 255, 0.4);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.35);
}

.landing-card__quantity-btn:not(:disabled):active {
  transform: translateY(0);
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.4);
}

.landing-card__quantity-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.landing-card__quantity-value {
  font-size: 1.15rem;
  font-weight: 600;
}

.landing-card__order {
  border: none;
  border-radius: 999px;
  padding: 0.75rem 1.4rem;
  background: linear-gradient(120deg, var(--ambifox-color), #ff9a4d);
  color: #0c0b0b;
  cursor: pointer;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  transition: transform 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 20px 34px rgba(255, 128, 38, 0.35);
}

.landing-card__order-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #1a1201;
}

.landing-card__order-icon svg {
  width: 18px;
  height: 18px;
}

.landing-card__order:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.landing-card__order:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 28px 44px rgba(255, 137, 42, 0.45);
}

.landing-card__cancel {
  border: 1px solid rgba(255, 255, 255, 0.32);
  border-radius: 999px;
  padding: 0.55rem 1.2rem;
  background: rgba(255, 255, 255, 0.01);
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  transition: all 0.2s ease;
}

.landing-card__cancel-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.landing-card__cancel-icon svg {
  width: 16px;
  height: 16px;
}

.landing-card__cancel:hover {
  border-color: rgba(255, 255, 255, 0.6);
  transform: translateY(-1px);
}

.landing-card__cancel:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.landing-card__countdown {
  margin: 0.4rem 0 0;
  font-size: 0.9rem;
  font-variant-numeric: tabular-nums;
  color: rgba(255, 255, 255, 0.7);
}

.landing-card__status {
  margin-top: 0.75rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  min-height: 1.1rem;
}

.landing-card--weekly {
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: linear-gradient(
    140deg,
    rgba(17, 21, 41, 0.95),
    rgba(14, 20, 36, 0.88)
  );
}

.landing__weekly {
  max-width: 1160px;
  margin: 2.5rem auto 0;
}

.landing-card__footer--weekly {
  flex-direction: column;
  align-items: flex-start;
  gap: 0.6rem;
}

.landing-card__weekly-hint {
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  font-style: italic;
}

.landing__history {
  max-width: 1160px;
  margin: 3rem auto 0;
  background: rgba(9, 15, 32, 0.9);
  border-radius: 1.8rem;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 28px 60px rgba(0, 0, 0, 0.65);
}

.landing__history-head {
  margin-bottom: 1.25rem;
}

.landing__history-head h2 {
  margin: 0;
  font-size: 1.4rem;
  letter-spacing: 0.01em;
}

.landing__history-head p {
  margin: 0.3rem 0 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.95rem;
}

.landing__notes {
  grid-column: 1 / -1;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 1.25rem;
  padding: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

@media (min-width: 1100px) {
  .landing__menu {
    max-width: 1320px;
    gap: 2rem;
  }

  .landing__weekly {
    max-width: 1320px;
  }
}

@media (max-width: 640px) {
  .landing {
    padding: 2rem 1rem 3rem;
  }

  .landing__topbar {
    justify-content: center;
    flex-wrap: wrap;
  }

  .landing__hero {
    text-align: center;
  }

  .landing__brand {
    justify-content: center;
  }

  .landing__insight-card {
    justify-content: center;
  }

  .landing-card__actions {
    flex-direction: column;
    align-items: stretch;
  }
}

@keyframes floatCard {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}
</style>
